import { isEmpty } from "lodash";
import { Control, FieldPath, FieldValues, RegisterOptions } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/common/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../ui/common/select";

interface FormSelectOption {
  key?: string | number;
  value: string | number;
  label?: string;
  text?: string;
}

interface FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  control: Control<TFieldValues>;
  name: TName;
  label?: string | null;
  required?: boolean;
  description?: string;
  placeholder?: string;
  options: FormSelectOption[];
  errorMessage?: string;
  disabled?: boolean;
  resetKey?: number | string;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  onChange?: (value: string) => void;
  className?: string;
}

export function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  required = false,
  description,
  placeholder = "Select an option",
  options = [],
  errorMessage,
  disabled = false,
  resetKey,
  rules,
  onChange,
  className
}: FormSelectProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      rules={{
        required: required ? errorMessage || "This field is required" : false,
        ...rules
      }}
      render={({ field, fieldState }: any) => (
        <FormItem>
          <FormLabel>
            {label}
            {required && !isEmpty(label) && <span className="tw-text-destructive tw-ml-0.5">*</span>}
          </FormLabel>
          <Select
            key={resetKey !== undefined ? `${name}-${resetKey}` : name}
            onValueChange={value => {
              field.onChange(value);
              onChange?.(value);
            }}
            value={
              field.value === undefined || field.value === null || field.value === ""
                ? undefined
                : String(field.value)
            }
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className={`tw-w-full !tw-h-10 tw-px-2 tw-overflow-clip ${className}`}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map(option => (
                <SelectItem key={String(option.key ?? option.value)} value={String(option.value)}>
                  {option.label || option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {description && <p className="tw-text-sm tw-text-muted-foreground">{description}</p>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
